import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type IChatbotSettings } from 'entities/Chatbot'
import { type AdvancedSettingsStore } from './advancedSettingsStore'

export class PromptSettingsStore {
  private _errorsMap: Map<string, string> = new Map()
  private _promptLengthMax = 7000
  private _instructions = ''
  private _disposeGetInstructions: IReactionDisposer | null = null
  private _disposeReachLimit: IReactionDisposer | null = null
  private _originalPrompts: IChatbotSettings | null = null

  constructor(private readonly _advancedSettingsStore: AdvancedSettingsStore) {
    makeAutoObservable(this)

    this.reactionGetInstructions()
    this.reactionReachLimit()
  }

  reactionGetInstructions = () => {
    this._disposeGetInstructions?.()
    this._disposeGetInstructions = reaction(
      () => this._advancedSettingsStore.agentInstruction,
      (instruction) => (this._instructions = instruction)
    )
  }

  reactionReachLimit = () => {
    this._disposeReachLimit?.()
    this._disposeReachLimit = reaction(
      () => this.reachMaxLength,
      (reachMaxLength) => {
        if (reachMaxLength) {
          this.setError(
            'promptsLength',
            'Your prompt exceeds 7,000 characters. Shorten it to continue.'
          )
        } else {
          this.removeError('promptsLength')
        }
      }
    )
  }

  init = (data: IChatbotSettings | null) => {
    this._instructions = data?.instructions?.value ?? this._advancedSettingsStore.agentInstruction
    this._originalPrompts = data
    this.errorsMap.clear()
  }

  get errorsMap() {
    return this._errorsMap
  }

  get promptLengthMax() {
    return this._promptLengthMax
  }

  get instructions() {
    return this._instructions
  }

  get promptLength() {
    return this.instructions.length
  }

  get reachMaxLength() {
    return this.promptLength > this.promptLengthMax
  }

  get payload() {
    return {
      advanced_prompt: {
        settings: {
          instructions: {
            value: this._instructions,
          },
        },
      },
    }
  }

  setInstructions = (value: string) => {
    this._instructions = value
    this.removeError('instructions')
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  validate = () => {
    if (this._instructions.length === 0) {
      this.setError('instructions', 'The Instructions field is required.')
      return false
    }

    return true
  }

  reset = () => {
    this._instructions =
      this._originalPrompts?.instructions?.value ?? this._advancedSettingsStore.agentInstruction
    this.removeError('instructions')
  }

  dispose = () => {
    this._disposeGetInstructions?.()
    this._disposeReachLimit?.()
  }
}
